/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/count_distinct.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Returns the approximate number of distinct values.
 *
 * Note: Counts are approximate.
 *
 * @example
 * FROM hosts
 * | STATS COUNT_DISTINCT(ip0), COUNT_DISTINCT(ip1)
 *
 * @example
 * FROM hosts
 * | STATS COUNT_DISTINCT(ip0, 80000), COUNT_DISTINCT(ip1, 5)
 *
 * @example
 * ROW words="foo;bar;baz;qux;quux;foo"
 * | STATS distinct_word_count = COUNT_DISTINCT(SPLIT(words, ";"))
 */
const definition: FunctionDefinition = {
  type: 'agg',
  name: 'count_distinct',
  signatures: [
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'boolean',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'date_nanos',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'double',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'flattened',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'integer',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'ip',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'keyword',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'long',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'text',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'precision',
          type: 'integer',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'precision',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
    {
      params: [
        {
          name: 'field',
          type: 'version',
          optional: false,
        },
        {
          name: 'precision',
          type: 'unsigned_long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'long',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
